import React from 'react';

export default function BuilderToolbar({
  page,
  onPageChange,
  onClearCanvas,
  onResetLayout,
  onAlign,
  onApplyPreset,
  onDuplicateSelected,
  onDeleteSelected,
  onToggleLockSelected,
  onBringForward,
  onSendBackward,
  onDistributeHorizontal,
  onDistributeVertical,
  hasSelected = false,
  hasMultipleSelected = false,
  selectedLocked = false,
  saveState = 'saved',
  selectedBlockLabel = '',
}) {
  const saveLabel = saveState === 'saving'
    ? 'Menyimpan...'
    : saveState === 'pending'
      ? 'Perubahan belum tersimpan'
      : 'Tersimpan';
  const saveColor = saveState === 'saving'
    ? 'text-amber-200'
    : saveState === 'pending'
      ? 'text-rose-200'
      : 'text-emerald-200';

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-cyan-300/20 bg-[#061020]/70 p-2 text-xs">
      <label className="text-slate-300">Page Size</label>
      <select
        value={page.page_size}
        onChange={(e) => onPageChange({ page_size: e.target.value })}
        className="rounded-md border border-cyan-300/25 bg-[#081427] px-2 py-1 text-slate-100"
      >
        <option value="A4">A4</option>
        <option value="A5">A5</option>
        <option value="thermal_58mm">thermal_58mm</option>
        <option value="thermal_80mm">thermal_80mm</option>
        <option value="custom">custom</option>
      </select>

      <label className="text-slate-300">Orientation</label>
      <select
        value={page.orientation}
        onChange={(e) => onPageChange({ orientation: e.target.value })}
        className="rounded-md border border-cyan-300/25 bg-[#081427] px-2 py-1 text-slate-100"
      >
        <option value="portrait">Portrait</option>
        <option value="landscape">Landscape</option>
      </select>

      <label className="inline-flex items-center gap-1 text-slate-300">
        <input
          type="checkbox"
          checked={Boolean(page.grid_enabled)}
          onChange={(e) => onPageChange({ grid_enabled: e.target.checked })}
        />
        Grid
      </label>

      <label className="inline-flex items-center gap-1 text-slate-300">
        <input
          type="checkbox"
          checked={Boolean(page.snap_enabled)}
          onChange={(e) => onPageChange({ snap_enabled: e.target.checked })}
        />
        Snap
      </label>

      <label className="text-slate-300">Grid</label>
      <select
        value={Number(page.grid_size || 8)}
        onChange={(e) => onPageChange({ grid_size: Number(e.target.value || 8) })}
        className="w-16 rounded-md border border-cyan-300/25 bg-[#081427] px-2 py-1 text-slate-100"
      >
        <option value={4}>4</option>
        <option value={8}>8</option>
        <option value={10}>10</option>
        <option value={12}>12</option>
      </select>

      <label className="text-slate-300">Zoom</label>
      <input
        type="range"
        min="0.5"
        max="1.5"
        step="0.1"
        value={Number(page.zoom || 1)}
        onChange={(e) => onPageChange({ zoom: Number(e.target.value) })}
      />

      <div className="mx-1 h-5 w-px bg-cyan-400/20" />
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" disabled={!hasSelected} onClick={() => onAlign?.('left')} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Left</button>
        <button type="button" disabled={!hasSelected} onClick={() => onAlign?.('center_x')} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Center X</button>
        <button type="button" disabled={!hasSelected} onClick={() => onAlign?.('right')} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Right</button>
        <button type="button" disabled={!hasSelected} onClick={() => onAlign?.('top')} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Top</button>
        <button type="button" disabled={!hasSelected} onClick={() => onAlign?.('center_y')} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Center Y</button>
        <button type="button" disabled={!hasSelected} onClick={() => onAlign?.('bottom')} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Bottom</button>
      </div>

      <div className="mx-1 h-5 w-px bg-cyan-400/20" />
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" disabled={!hasMultipleSelected} onClick={onDistributeHorizontal} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Dist H</button>
        <button type="button" disabled={!hasMultipleSelected} onClick={onDistributeVertical} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Dist V</button>
      </div>

      <div className="mx-1 h-5 w-px bg-cyan-400/20" />
      <label className="text-slate-300">Preset</label>
      <select
        disabled={!hasSelected}
        defaultValue=""
        onChange={(e) => {
          const value = e.target.value;
          if (value) onApplyPreset?.(value);
          e.target.value = '';
        }}
        className="rounded-md border border-cyan-300/25 bg-[#081427] px-2 py-1 text-slate-100 disabled:opacity-40"
      >
        <option value="" disabled>Pilih</option>
        <option value="center">Center</option>
        <option value="header">Header</option>
        <option value="footer">Footer</option>
        <option value="top_left">Top Left</option>
        <option value="top_right">Top Right</option>
        <option value="bottom_left">Bottom Left</option>
        <option value="bottom_right">Bottom Right</option>
        <option value="price_badge_corner">Price Badge Corner</option>
        <option value="qr_panel_corner">QR Panel Corner</option>
        <option value="credential_center">Credential Center</option>
      </select>

      <div className="ml-auto flex items-center gap-2">
        <span className={`rounded-md border border-cyan-300/20 bg-cyan-500/5 px-2 py-1 ${saveColor}`}>
          {saveLabel}
        </span>
        {selectedBlockLabel ? (
          <span className="rounded-md border border-cyan-300/20 bg-cyan-500/5 px-2 py-1 text-cyan-100">
            Pilih: {selectedBlockLabel}
          </span>
        ) : null}
        <button type="button" disabled={!hasSelected} onClick={onSendBackward} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Send Back</button>
        <button type="button" disabled={!hasSelected} onClick={onBringForward} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Bring Front</button>
        <button type="button" disabled={!hasSelected} onClick={onToggleLockSelected} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">{selectedLocked ? 'Unlock' : 'Lock'}</button>
        <button type="button" disabled={!hasSelected} onClick={onDuplicateSelected} className="rounded border border-cyan-300/25 bg-cyan-500/10 px-2 py-1 text-cyan-100 disabled:opacity-40">Duplicate</button>
        <button type="button" disabled={!hasSelected} onClick={onDeleteSelected} className="rounded border border-rose-300/25 bg-rose-500/10 px-2 py-1 text-rose-200 disabled:opacity-40">Delete</button>
        <button type="button" onClick={onClearCanvas} className="rounded-lg border border-cyan-300/30 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-100 hover:border-cyan-300/60">
          Clear Canvas
        </button>
        <button type="button" onClick={onResetLayout} className="rounded-lg border border-cyan-300/30 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-100 hover:border-cyan-300/60">
          Reset Layout
        </button>
      </div>
    </div>
  );
}
